<template>
  <div>
    <Head :title="title" />

    <div class="eu-wrap">
      <form @submit.prevent="update">

        <!-- Top: Avatar + Name + Role Badge -->
        <div class="eu-top">
          <div class="eu-avatar">
            <img v-if="photoPreview || user.photo_path" :src="photoPreview || user.photo_path" class="w-full h-full object-cover" />
            <span v-else class="eu-initials">{{ (user.first_name || '?')[0].toUpperCase() }}</span>
          </div>
          <div class="eu-top-info">
            <div class="eu-top-name">{{ user.first_name }} {{ user.last_name }}</div>
            <div class="eu-top-meta">
              <span class="eu-top-email">{{ user.email }}</span>
              <span v-if="user.role" class="eu-badge eu-badge--green">{{ user.role.name }}</span>
            </div>
          </div>
        </div>

        <!-- Form Body -->
        <div class="eu-body">

          <!-- Section: Personal -->
          <p class="eu-section-label">Personal</p>
          <div class="eu-grid eu-grid--3">
            <text-input v-model="form.first_name" :error="form.errors.first_name" :label="__('First name')" />
            <text-input v-model="form.last_name"  :error="form.errors.last_name"  :label="__('Last name')" />
            <text-input v-model="form.email"       :error="form.errors.email"       :label="__('Email')" />
            <text-input v-model="form.phone"       :error="form.errors.phone"       :label="__('Phone')" />
            <text-input v-model="form.city"        :error="form.errors.city"        :label="__('City')" />
            <select-input v-model="form.country_id" :error="form.errors.country_id" :label="__('Country')">
              <option :value="null" />
              <option v-for="c in countries" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
            </select-input>
            <text-input v-model="form.address" :error="form.errors.address" :label="__('Address')" class="eu-col-span-3" />
          </div>

          <div class="eu-sep"></div>

          <!-- Section: Account -->
          <p class="eu-section-label">Account</p>
          <div class="eu-grid eu-grid--3">
            <text-input v-model="form.password" :error="form.errors.password" type="password" autocomplete="new-password" :label="__('New Password')" />
            <!-- Photo picker -->
            <div class="eu-photo-cell">
              <label class="form-label">Photo</label>
              <input ref="photoInput" type="file" accept="image/*" class="hidden" @change="onPhotoChange" />
              <div class="eu-photo-picker" @click="$refs.photoInput.click()" title="Click to change photo">
                <img v-if="photoPreview || user.photo_path" :src="photoPreview || user.photo_path" class="eu-photo-picker-img" />
                <div v-else class="eu-photo-picker-initials">{{ (user.first_name || '?')[0].toUpperCase() }}</div>
                <div class="eu-photo-picker-overlay">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="eu-photo-cam-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                  </svg>
                </div>
              </div>
              <div v-if="form.photo" class="eu-photo-info">
                <span class="eu-photo-fname">{{ form.photo.name }}</span>
                <button type="button" class="eu-photo-clear" @click="removePhoto" title="Remove">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3 h-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div v-if="form.errors.photo" class="form-error">{{ form.errors.photo }}</div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="eu-footer">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            {{ __('Update Profile') }}
          </loading-button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: { Head, LoadingButton, SelectInput, TextInput },
  layout: Layout,
  props: {
    user: Object,
    auth: Object,
    countries: Array,
    cities: Array,
    title: String,
  },
  remember: 'form',
  data() {
    return {
      photoPreview: null,
      form: this.$inertia.form({
        _method: 'put',
        first_name: this.user.first_name,
        last_name:  this.user.last_name,
        email:      this.user.email,
        phone:      this.user.phone,
        city:       this.user.city,
        address:    this.user.address,
        country_id: this.user.country_id,
        password:   '',
        role:       this.user.role,
        role_id:    this.user.role_id,
        photo:      null,
      }),
    }
  },
  methods: {
    update() {
      this.form.post(this.route('users.update', this.user.id), {
        onSuccess: () => { this.form.reset('password', 'photo'); this.photoPreview = null },
      })
    },
    onPhotoChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.form.photo = file
      this.photoPreview = URL.createObjectURL(file)
    },
    removePhoto() {
      this.form.photo = null
      this.photoPreview = null
      this.$refs.photoInput.value = ''
    },
  },
}
</script>
